
import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useRive } from "@rive-app/react-canvas"
import { parseSRT } from "./SRTParser"
import EnhancedAudioControls from "./EnhancedAudioControls"
import Confetti from "./Confetti"
import SuperSimpleSynchronizer from "./SuperSimpleSynchronizer"
import { getMediaPlayerMode } from "./MediaPlayerModes"
import "./components/TeachingAvatar.css" // Import the CSS file

const MAS_STATES = {
  idle: { input: "idle", type: "trigger" },
  wave: { input: "Wave", type: "trigger" },
  jump: { input: "Jump", type: "trigger" },
  really: { input: "Really", type: "trigger" },
  bow: { input: "Bow", type: "trigger" },
  defeated: { input: "defeated", type: "trigger" },
  // Keep these existing states for backward compatibility
  happy: { input: "Wave", type: "trigger" }, // Map to wave
  sad: { input: "Really", type: "trigger" }, // Map to defeated
  follow: { input: "idle", type: "trigger" }, // Map to idle
  smile: { input: "Wave", type: "trigger" }, // Map to wave
  talk: { input: "Talk", type: "trigger" }, // Keep talk as is
  hover: { input: "idle", type: "trigger" }, // Map to idle
  thinking: { input: "Jump", type: "trigger" }, // Map to really?
}

const TeachingAvatar = ({
  mainContent,
  visualContent,
  spokenContent,
  quizOptions,
  onSelectAnswer,
  selectedAnswer,
  isCorrect,
  isChecking,
  isCelebration,
  isSuccess,
  audioSrc,
  srtSrc,
  initialState = "idle",
  state,
  audioControlsRef: externalAudioControlsRef,
  initialControlsVisible = true,
  lessonId,
  // Add this line to receive the new prop
  hideAudioControls = false,
}) => {
  // Initialize showSubtitles to false by default
  const [showSubtitles, setShowSubtitles] = useState(false)
  const [currentCaption, setCurrentCaption] = useState(spokenContent || "")
  const [subtitles, setSubtitles] = useState([])
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [currentAudioTime, setCurrentAudioTime] = useState(0)
  const [isAudioPlaying, setIsAudioPlaying] = useState(false)
  const [currentLine, setCurrentLine] = useState(0)
  const [totalLines, setTotalLines] = useState(1)
  const [showControls, setShowControls] = useState(initialControlsVisible)
  const [isSrtOnlyMode, setIsSrtOnlyMode] = useState(false)
  const [playerMode, setPlayerMode] = useState("text-only")
  const [isMobile, setIsMobile] = useState(false)
  // New state for feedback visibility - this will naturally reset on component re-render
  const [showFeedback, setShowFeedback] = useState(false)

  const videoContainerRef = useRef(null)
  const internalAudioControlsRef = useRef(null)
  const bubbleRef = useRef(null)
  const contentContainerRef = useRef(null)
  const lastContentRef = useRef({
    mainContent,
    audioSrc,
    srtSrc,
    isCelebration: false,
    isSuccess: false,
  })
  const srtTimerRef = useRef(null)
  const srtStartTimeRef = useRef(0)
  const lessonIdRef = useRef(lessonId)

  // Use the external ref if provided, otherwise use our internal one
  const audioControlsRef = externalAudioControlsRef || internalAudioControlsRef

  const { rive, RiveComponent } = useRive({
    src: "/avatar/my_avatar.riv",
    stateMachines: "State Machine 1",
    autoplay: true,
  })

  // Detect the media player mode
  useEffect(() => {
    const mode = getMediaPlayerMode(audioSrc, srtSrc)
    setPlayerMode(mode)
    setIsSrtOnlyMode(mode === "srt-only")
  }, [audioSrc, srtSrc])

  // Handle lesson changes
  useEffect(() => {
    if (lessonIdRef.current !== lessonId) {
      // We're in a new lesson, reset to defaults based on device type
      if (window.innerWidth >= 768) {
        setShowSubtitles(true)
      } else {
        setShowSubtitles(false)
      }

      setShowControls(initialControlsVisible)

      // Update the ref
      lessonIdRef.current = lessonId
    }
  }, [lessonId, initialControlsVisible])

  // Add your new useEffect hook RIGHT HERE
  useEffect(() => {
    // Check if we're on mobile and initialize subtitle visibility accordingly
    const checkIsMobile = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)

      // Always set the initial state based on device type
      if (mobile) {
        setShowSubtitles(false)
      } else {
        setShowSubtitles(true)
      }
    }

    // Run check once on component mount
    checkIsMobile()

    // Add resize listener to adjust if window is resized
    window.addEventListener("resize", checkIsMobile)

    return () => {
      window.removeEventListener("resize", checkIsMobile)
    }
  }, [])

  // Special handling for celebration/success screens - always show subtitles
  useEffect(() => {
    if (isCelebration || isSuccess) {
      setShowSubtitles(true)
    } else if (isMobile) {
      // On regular screens, hide subtitles on mobile by default
      setShowSubtitles(false)
    }
  }, [isCelebration, isSuccess, isMobile])

  // Update feedback visibility when checking answers
  useEffect(() => {
    const isQuizContent = quizOptions && quizOptions.length > 0

    if (isMobile && isQuizContent && isChecking && selectedAnswer !== null) {
      setShowFeedback(true)
    } else {
      setShowFeedback(false)
    }
  }, [isMobile, quizOptions, isChecking, selectedAnswer])

  useEffect(() => {
    // Function to check if we're on a quiz page
    const isQuizContent = quizOptions && quizOptions.length > 0

    // Add a class to the parent container for quiz-specific styling
    const avatarContainer = document.querySelector(".avatar-container")
    if (avatarContainer) {
      if (isQuizContent) {
        avatarContainer.classList.add("quiz-content")
      } else {
        avatarContainer.classList.remove("quiz-content")
      }
    }

    // Get the avatar element
    const avatarElement = document.querySelector(".avatar-fixed-container")

    if (avatarElement) {
      // On mobile, we'll use CSS to handle the visibility and positioning
      // This ensures the speech bubble remains visible while the avatar is hidden
      if (window.innerWidth < 768 && isQuizContent) {
        // Make sure speech bubble is visible when checking answers
        if (isChecking && selectedAnswer !== null) {
          setShowSubtitles(true)
        }
      }
    }

    // Add resize listener to handle orientation changes
    const handleResize = () => {
      if (isQuizContent && window.innerWidth < 768) {
        // Make sure speech bubble is visible when checking answers on mobile
        if (isChecking && selectedAnswer !== null) {
          setShowSubtitles(true)
        }
      }
    }

    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [quizOptions, isChecking, selectedAnswer])

  // Primary content change detection effect - very important for page transitions
  useEffect(() => {
    // Check if we've moved to a different page/content
    const contentChanged =
      lastContentRef.current.mainContent !== mainContent ||
      lastContentRef.current.audioSrc !== audioSrc ||
      lastContentRef.current.srtSrc !== srtSrc ||
      lastContentRef.current.isCelebration !== isCelebration ||
      lastContentRef.current.isSuccess !== isSuccess

    // Only run full reset if content has actually changed
    if (contentChanged) {
      // Immediately reset subtitle state to prevent flashing old subtitles
      setSubtitles([])
      setCurrentCaption(spokenContent || "")
      setCurrentLine(0)
      setTotalLines(1)
      setCurrentAudioTime(0)
      setIsAudioPlaying(false)
      // Reset feedback visibility on content change
      setShowFeedback(false)

      // Reset subtitle visibility based on device type
      if (window.innerWidth < 768) {
        setShowSubtitles(false)
      } else {
        setShowSubtitles(true)
      }

      // Update reference to current content
      lastContentRef.current = {
        mainContent,
        audioSrc,
        srtSrc,
        isCelebration,
        isSuccess,
      }

      // Clear any SRT timer
      if (srtTimerRef.current) {
        clearInterval(srtTimerRef.current)
        srtTimerRef.current = null
      }
    }
  }, [mainContent, audioSrc, srtSrc, spokenContent, isCelebration, isSuccess])

  // Monitor content type changes to hide feedback on non-quiz pages
  useEffect(() => {
    // Check if we're on a quiz page
    const isQuizContent = quizOptions && quizOptions.length > 0

    // Hide feedback when not on a quiz page
    if (!isQuizContent) {
      setShowFeedback(false)
    }

    // Also hide feedback on celebration/success pages
    if (isCelebration || isSuccess) {
      setShowFeedback(false)
    }
  }, [quizOptions, isCelebration, isSuccess])

  // Set controls visibility from prop when it changes
  useEffect(() => {
    setShowControls(initialControlsVisible)
  }, [initialControlsVisible])

  // When spokenContent changes, update the caption
  useEffect(() => {
    if (spokenContent) {
      setCurrentCaption(spokenContent)

      // If it's a quiz and we're checking an answer, always show the subtitles
      // This is especially important on mobile
      if (isChecking && quizOptions && selectedAnswer !== null) {
        setShowSubtitles(true)
      }
    }
  }, [spokenContent, isChecking, quizOptions, selectedAnswer])

  // Update avatar animation state based on current state
  const updateAvatarState = (isTalking) => {
    if (!rive) return

    if (isTalking) {
      // Use talk state when playing/speaking
      const input = rive.stateMachineInputs("State Machine 1").find((input) => input.name === "talk")
      if (input) {
        input.fire()
      }
    } else if (state) {
      // Return to specified state when not speaking
      const stateConfig = MAS_STATES[state]
      if (stateConfig) {
        const input = rive.stateMachineInputs("State Machine 1").find((input) => input.name === stateConfig.input)
        if (input) {
          input.fire()
        }
      }
    }
  }

  // Update avatar state when state prop changes
  useEffect(() => {
    if (rive && state) {
      const stateConfig = MAS_STATES[state]
      if (stateConfig) {
        const input = rive.stateMachineInputs("State Machine 1").find((input) => input.name === stateConfig.input)
        if (input) {
          input.fire()
        }
      }
    } else if (rive && isAudioPlaying) {
      // If playing audio/speaking, use talk state
      updateAvatarState(true)
    }
  }, [rive, state, isAudioPlaying])

  // Load subtitles when srtSrc changes - after state reset
  useEffect(() => {
    const loadSubtitles = async () => {
      if (!srtSrc) return

      try {
        const response = await fetch(srtSrc)
        const srtContent = await response.text()
        const parsedSubtitles = parseSRT(srtContent)

        // Only update state if we're still on the same content
        if (lastContentRef.current.srtSrc === srtSrc) {
          setSubtitles(parsedSubtitles)
          if (parsedSubtitles.length > 0) {
            setCurrentCaption(parsedSubtitles[0].text)
            setTotalLines(parsedSubtitles.length)
          }
        }
      } catch (error) {
        console.error("Error loading subtitles:", error)
      }
    }

    loadSubtitles()
  }, [srtSrc])

  // Handle SRT-only mode timer
  useEffect(() => {
    // Only start timer for SRT-only mode when playing
    if (playerMode !== "srt-only" || !isAudioPlaying) {
      if (srtTimerRef.current) {
        clearInterval(srtTimerRef.current)
        srtTimerRef.current = null
      }
      return
    }

    // Start a new timer for SRT playback
    if (!srtTimerRef.current) {
      srtStartTimeRef.current = Date.now() - currentAudioTime * 1000

      srtTimerRef.current = setInterval(() => {
        const newTime = (Date.now() - srtStartTimeRef.current) / 1000
        setCurrentAudioTime(newTime)

        // Check if we've reached the end of all subtitles
        if (subtitles.length > 0) {
          const lastSub = subtitles[subtitles.length - 1]
          if (lastSub && newTime > lastSub.end + 1) {
            // Auto-stop at the end of subtitles
            setIsAudioPlaying(false)

            // Clear the timer
            clearInterval(srtTimerRef.current)
            srtTimerRef.current = null

            // Update avatar animation
            updateAvatarState(false)
          }
        }
      }, 100)
    }

    return () => {
      if (srtTimerRef.current) {
        clearInterval(srtTimerRef.current)
        srtTimerRef.current = null
      }
    }
  }, [playerMode, isAudioPlaying, subtitles, currentAudioTime])

  // Find and update the current subtitle based on time
  useEffect(() => {
    if (!subtitles || subtitles.length === 0) return

    const currentSub = subtitles.find((sub) => currentAudioTime >= sub.start && currentAudioTime <= sub.end)

    if (currentSub) {
      setCurrentCaption(currentSub.text)
      // Update current line index for navigation controls
      const lineIndex = subtitles.findIndex((sub) => sub.text === currentSub.text)
      if (lineIndex !== -1 && lineIndex !== currentLine) {
        setCurrentLine(lineIndex)
      }
    } else if (!spokenContent) {
      // If no active subtitle and no fixed content, show first subtitle when time is before
      // the first subtitle, or the last subtitle when time is after the last one
      if (currentAudioTime < subtitles[0].start) {
        setCurrentCaption(subtitles[0].text)
        setCurrentLine(0)
      } else if (currentAudioTime > subtitles[subtitles.length - 1].end) {
        setCurrentCaption(subtitles[subtitles.length - 1].text)
        setCurrentLine(subtitles.length - 1)
      }
    }
  }, [currentAudioTime, subtitles, currentLine, spokenContent])

  // Add a subtle bounce effect to the speech bubble to draw attention
  useEffect(() => {
    if (bubbleRef.current && isAudioPlaying) {
      // Add an animation class every 5 seconds to draw attention
      const pulse = setInterval(() => {
        if (bubbleRef.current) {
          bubbleRef.current.classList.add("transition-transform")
          bubbleRef.current.classList.add("scale-105")

          setTimeout(() => {
            if (bubbleRef.current) {
              bubbleRef.current.classList.remove("scale-105")
            }
          }, 500)
        }
      }, 5000)

      return () => clearInterval(pulse)
    }
  }, [isAudioPlaying])

  // Expose methods to the parent through ref
  useEffect(() => {
    if (externalAudioControlsRef && externalAudioControlsRef.current) {
      externalAudioControlsRef.current = {
        pause: () => {
          // Directly update state without calling handlePlayStateChange
          setIsAudioPlaying(false)

          // If we have audio controls, use them directly to avoid recursion
          if (
            audioControlsRef.current &&
            audioControlsRef.current.pause &&
            audioControlsRef.current !== externalAudioControlsRef.current
          ) {
            audioControlsRef.current.pause()
          }

          // Clear SRT timer if active
          if (srtTimerRef.current) {
            clearInterval(srtTimerRef.current)
            srtTimerRef.current = null
          }

          // Update avatar animation directly
          updateAvatarState(false)
        },
        play: async () => {
          // Directly set playing state
          setIsAudioPlaying(true)

          // Only show subtitles automatically on desktop
          if (window.innerWidth >= 768) {
            setShowSubtitles(true)
          }

          if (
            audioControlsRef.current &&
            audioControlsRef.current.play &&
            audioControlsRef.current !== externalAudioControlsRef.current
          ) {
            await audioControlsRef.current.play()
          } else if (playerMode === "srt-only" && !srtTimerRef.current) {
            // Start SRT timer for SRT-only mode
            srtStartTimeRef.current = Date.now() - currentAudioTime * 1000

            // Update avatar animation directly
            updateAvatarState(true)

            // Start the SRT timer
            srtTimerRef.current = setInterval(() => {
              const newTime = (Date.now() - srtStartTimeRef.current) / 1000
              setCurrentAudioTime(newTime)

              // Check if we've reached the end
              if (subtitles.length > 0) {
                const lastSub = subtitles[subtitles.length - 1]
                if (lastSub && newTime > lastSub.end + 1) {
                  // Auto-stop at the end of subtitles
                  setIsAudioPlaying(false)
                  clearInterval(srtTimerRef.current)
                  srtTimerRef.current = null
                  updateAvatarState(false)
                }
              }
            }, 100)
          }
        },
        nextLine: () => {
          handleNavigate("next")
        },
        prevLine: () => {
          handleNavigate("prev")
        },
        forceShowSubtitles: () => {
          setShowSubtitles(true)
        },
      }
    }
  }, [externalAudioControlsRef, showControls, audioSrc, playerMode, currentAudioTime, subtitles])

  // Handle fullscreen toggling
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      videoContainerRef.current?.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange)
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
    }
  }, [])

  // Handle audio time updates to sync with subtitles
  const handleTimeUpdate = (currentTime) => {
    setCurrentAudioTime(currentTime)
  }

  // Non-recursive handlePlayStateChange
  const handlePlayStateChange = (isPlaying) => {
    // Set state directly
    setIsAudioPlaying(isPlaying)

    if (isPlaying) {
      // Only show subtitles automatically on desktop, not mobile
      if (window.innerWidth >= 768) {
        setShowSubtitles(true)
      }

      if (playerMode === "srt-only" && !srtTimerRef.current) {
        // Start SRT timer for SRT-only mode
        srtStartTimeRef.current = Date.now() - currentAudioTime * 1000

        srtTimerRef.current = setInterval(() => {
          const newTime = (Date.now() - srtStartTimeRef.current) / 1000
          setCurrentAudioTime(newTime)

          // Check if we've reached the end of all subtitles
          if (subtitles.length > 0) {
            const lastSub = subtitles[subtitles.length - 1]
            if (lastSub && newTime > lastSub.end + 1) {
              // Auto-stop at the end of subtitles - no recursive calls
              setIsAudioPlaying(false)

              // Clear the timer
              clearInterval(srtTimerRef.current)
              srtTimerRef.current = null

              // Update avatar animation directly
              updateAvatarState(false)
            }
          }
        }, 100)
      }

      // Update avatar animation directly
      updateAvatarState(true)
    } else {
      // Stop SRT timer if active
      if (srtTimerRef.current) {
        clearInterval(srtTimerRef.current)
        srtTimerRef.current = null
      }

      // Update avatar animation directly
      updateAvatarState(false)
    }
  }

  // Handle navigation between subtitle lines
  const handleNavigate = (direction) => {
    // Stop any active timer for SRT-only mode
    if (srtTimerRef.current) {
      clearInterval(srtTimerRef.current)
      srtTimerRef.current = null
    }

    if (direction === "next" && currentLine < totalLines - 1) {
      const nextLine = currentLine + 1
      setCurrentLine(nextLine)

      if (subtitles[nextLine]) {
        // If we have audio, seek to the start time of the next subtitle
        if (audioControlsRef.current && audioSrc) {
          const audio = document.querySelector("audio")
          if (audio) {
            audio.currentTime = subtitles[nextLine].start
            handleTimeUpdate(subtitles[nextLine].start)
          }
        } else if (playerMode === "srt-only") {
          // For SRT-only mode, just update the time
          setCurrentAudioTime(subtitles[nextLine].start)
          setCurrentCaption(subtitles[nextLine].text)

          // Restart timer if we were playing
          if (isAudioPlaying) {
            srtStartTimeRef.current = Date.now() - subtitles[nextLine].start * 1000
            srtTimerRef.current = setInterval(() => {
              const newTime = (Date.now() - srtStartTimeRef.current) / 1000
              setCurrentAudioTime(newTime)
            }, 100)
          }
        } else {
          // Otherwise just update the caption
          setCurrentCaption(subtitles[nextLine].text)
        }
      }
    } else if (direction === "prev" && currentLine > 0) {
      const prevLine = currentLine - 1
      setCurrentLine(prevLine)

      if (subtitles[prevLine]) {
        // If we have audio, seek to the start time of the previous subtitle
        if (audioControlsRef.current && audioSrc) {
          const audio = document.querySelector("audio")
          if (audio) {
            audio.currentTime = subtitles[prevLine].start
            handleTimeUpdate(subtitles[prevLine].start)
          }
        } else if (playerMode === "srt-only") {
          // For SRT-only mode, just update the time
          setCurrentAudioTime(subtitles[prevLine].start)
          setCurrentCaption(subtitles[prevLine].text)

          // Restart timer if we were playing
          if (isAudioPlaying) {
            srtStartTimeRef.current = Date.now() - subtitles[prevLine].start * 1000
            srtTimerRef.current = setInterval(() => {
              const newTime = (Date.now() - srtStartTimeRef.current) / 1000
              setCurrentAudioTime(newTime)
            }, 100)
          }
        } else {
          // Otherwise just update the caption
          setCurrentCaption(subtitles[prevLine].text)
        }
      }
    } else if (direction === "first" && subtitles.length > 0) {
      // Go to the first subtitle
      setCurrentLine(0)
      if (subtitles[0]) {
        // If we have audio, seek to the start time of the first subtitle
        if (audioControlsRef.current && audioSrc) {
          const audio = document.querySelector("audio")
          if (audio) {
            audio.currentTime = subtitles[0].start
            handleTimeUpdate(subtitles[0].start)
          }
        } else if (playerMode === "srt-only") {
          // For SRT-only mode, just update the time
          setCurrentAudioTime(subtitles[0].start)
          setCurrentCaption(subtitles[0].text)

          // Restart timer if we were playing
          if (isAudioPlaying) {
            srtStartTimeRef.current = Date.now() - subtitles[0].start * 1000
            srtTimerRef.current = setInterval(() => {
              const newTime = (Date.now() - srtStartTimeRef.current) / 1000
              setCurrentAudioTime(newTime)
            }, 100)
          }
        } else {
          // Otherwise just update the caption
          setCurrentCaption(subtitles[0].text)
        }
      }
    }
  }

  // Determine which type of content to show
  const contentType = quizOptions
    ? "quiz"
    : isCelebration || isSuccess
      ? "achievements"
      : visualContent?.visualizationType || visualContent?.chartData
        ? "chartVisualization"
        : "content"

  // Check if this is a fullscreen content
  const isFullscreenContent = visualContent?.fullscreenContent
  const isFullHeightVideo = visualContent?.videoUrl && visualContent?.fullHeightVideo

  // Render mobile quiz feedback component - now directly in the component
  const renderMobileQuizFeedback = () => {
    if (!isMobile || !quizOptions || !isChecking || selectedAnswer === null || !showFeedback) return null

    const feedbackText = quizOptions[selectedAnswer]?.feedback || ""
    const isAnswerCorrect = quizOptions[selectedAnswer]?.correct || false

    return (
      <div className="mobile-quiz-feedback active">
        <div
          className={`mobile-quiz-feedback-content ${isAnswerCorrect ? "mobile-quiz-feedback-correct" : "mobile-quiz-feedback-incorrect"}`}
        >
          <div className="mobile-quiz-feedback-text">{feedbackText}</div>
        </div>
      </div>
    )
  }

  // Hide feedback on celebration/success pages
  useEffect(() => {
    if (isCelebration || isSuccess) {
      setShowFeedback(false)
    }
  }, [isCelebration, isSuccess])

  return (
    <div className="avatar-container">
      <div className={`layout-standard`}>
        {/* For celebration/success screens, use a special centered layout */}
        {isCelebration || isSuccess ? (
          <div className="celebration-container">
            <div ref={contentContainerRef} className="celebration-content">
              {/* Confetti */}
              <Confetti count={75} />

              {/* Title */}
              <h2 className="celebration-title">{mainContent || visualContent?.title || "Goed Gedaan! 🎉"}</h2>

              {/* Center avatar */}
              <div className="celebration-avatar-space">
                {/* Speech bubble above avatar */}
                <AnimatePresence>
                  {(showSubtitles || true) && currentCaption && (
                    <motion.div
                      ref={bubbleRef}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="speech-bubble text-center after:left-1/2 after:-translate-x-1/2 active"
                    >
                      <motion.p
                        key={currentCaption}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className="speech-bubble-text"
                      >
                        {currentCaption || "Je hebt alle badges verdiend! Geweldig werk!"}
                      </motion.p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Avatar */}

                <motion.div
                  whileHover={{ scale: 1.05 }}
                  onClick={() => {
                    // Toggle playback
                    const newPlayingState = !isAudioPlaying
                    setIsAudioPlaying(newPlayingState)
                    handlePlayStateChange(newPlayingState)

                    // Always show speech bubble when avatar is clicked
                    setShowSubtitles(true)
                  }}
                  className="avatar-button avatar-play-button"
                >
                  <RiveComponent style={{ width: "100%", height: "100%" }} />
                </motion.div>

                {/* Audio controls */}
                {!hideAudioControls && (
                  <EnhancedAudioControls
                    ref={audioControlsRef}
                    audioSrc={audioSrc}
                    srtSrc={srtSrc}
                    lessonId={lessonId}
                    onPlayStateChange={handlePlayStateChange}
                    onSubtitlesToggle={() => setShowSubtitles(!showSubtitles)}
                    onTimeUpdate={handleTimeUpdate}
                    onNavigate={handleNavigate}
                    hasNextLine={currentLine < totalLines - 1}
                    hasPrevLine={currentLine > 0}
                    initialShowControls={showControls}
                    isMobile={window.innerWidth < 768}
                  />
                )}
              </div>

              {/* Achievement badges in a row at the bottom */}
              <div className="celebration-badges">
                {visualContent?.achievements &&
                  visualContent.achievements.map((achievement, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 + index * 0.15 }}
                      className="celebration-badge"
                    >
                      <span className="celebration-badge-icon">{achievement.icon}</span>
                      <span className="celebration-badge-text">{achievement.title}</span>
                    </motion.div>
                  ))}
              </div>
            </div>
          </div>
        ) : (
          /* NORMAL LAYOUT FOR NON-CELEBRATION SCREENS */
          <>
            {/* CONTENT COLUMN */}
            <div className={isFullscreenContent ? "content-column-fullwidth" : "content-column"}>
              <div
                ref={contentContainerRef}
                className={`content-container ${isFullHeightVideo ? "content-container-fullheight" : "content-container-standard"}`}
              >
                <SuperSimpleSynchronizer
                  key={`synchronizer-${lessonId}`}
                  type={contentType}
                  content={{
                    ...visualContent,
                    title: mainContent || visualContent?.title,
                    options: quizOptions,
                    question: visualContent?.question,
                    subQuestion: visualContent?.subQuestion,
                    achievements: visualContent?.achievements,
                    fullHeightVideo: isFullHeightVideo,
                  }}
                  audioTime={currentAudioTime}
                  isPlaying={isAudioPlaying}
                  srtSrc={srtSrc}
                  currentLine={currentLine}
                  onSelectAnswer={onSelectAnswer}
                  selectedAnswer={selectedAnswer}
                  isChecking={isChecking}
                  videoContainerRef={videoContainerRef}
                  isFullscreen={isFullscreen}
                  toggleFullscreen={toggleFullscreen}
                  onStepChange={(nextStep) => {
                    if (visualContent && typeof visualContent.onStepChange === "function") {
                      visualContent.onStepChange(nextStep)
                    }
                  }}
                />
              </div>
            </div>

            {/* AVATAR COLUMN - Fixed position at bottom of screen */}
            {!isFullscreenContent && (
              <div className="avatar-column">
                <div className="avatar-fixed-container">
                  <AnimatePresence mode="wait">
                    {showSubtitles && currentCaption && (
                      <motion.div
                        ref={bubbleRef}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className={`speech-bubble ${isChecking ? "speech-bubble-checking" : ""} ${showSubtitles ? "active" : ""}`}
                      >
                        <motion.p
                          key={currentCaption}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                          className="speech-bubble-text"
                        >
                          {currentCaption}
                        </motion.p>

                        {/* Line indicator */}
                        {totalLines > 1 && (
                          <div className="speech-bubble-indicators">
                            {Array.from({ length: totalLines }).map((_, i) => (
                              <div
                                key={i}
                                className={`bubble-indicator ${i === currentLine ? "bubble-indicator-active" : ""}`}
                              />
                            ))}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Avatar with integrated control bar */}
                  <div className="relative">
                    <div className="avatar-wrapper">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        onClick={() => {
                          // On both mobile and desktop, toggle playback
                          const newPlayingState = !isAudioPlaying
                          setIsAudioPlaying(newPlayingState)
                          handlePlayStateChange(newPlayingState)
                        }}
                        className="avatar-button avatar-play-button"
                      >
                        <RiveComponent style={{ width: "100%", height: "100%" }} />
                      </motion.button>

                      {/* Audio controls - integrated with the avatar */}
                      {!hideAudioControls && (
                        <EnhancedAudioControls
                          ref={audioControlsRef}
                          audioSrc={audioSrc}
                          srtSrc={srtSrc}
                          lessonId={lessonId}
                          onPlayStateChange={handlePlayStateChange}
                          onSubtitlesToggle={() => setShowSubtitles(!showSubtitles)} // This is the ONLY place that should toggle subtitles
                          onTimeUpdate={handleTimeUpdate}
                          onNavigate={handleNavigate}
                          hasNextLine={currentLine < totalLines - 1}
                          hasPrevLine={currentLine > 0}
                          initialShowControls={showControls}
                          isMobile={window.innerWidth < 768}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Render mobile quiz feedback directly in the component */}
      {renderMobileQuizFeedback()}
    </div>
  )
}

export default TeachingAvatar

